import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { serviceRequestService } from '../../services/serviceRequestService'
import { useNotificationStore } from '../../stores/notificationStore'
import type { ServiceRequest, ServiceRequestStatus, DeclineRequest } from '../../types/serviceRequest'
import { REQUEST_TYPE_OPTIONS } from '../../types/seeker'
import RequestDetailDrawer from '../../components/connections/RequestDetailDrawer'
import {
  Check,
  X,
  MessageSquare,
  Clock,
  MapPin,
  Loader2,
  Inbox,
  CheckCircle,
  XCircle,
  AlertCircle,
  Briefcase,
  CreditCard,
  Hourglass,
} from 'lucide-react'
import toast from 'react-hot-toast'

type ExpertTab = 'new' | 'accepted' | 'awaiting_payment' | 'in_progress' | 'history'
type SeekerTab = 'new' | 'accepted' | 'awaiting_payment' | 'in_progress' | 'history'
type Tab = ExpertTab | SeekerTab

export default function ConnectionsPage() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const isTalent = user?.role === 'talent'

  const [activeTab, setActiveTab] = useState<Tab>('new')
  const [requests, setRequests] = useState<ServiceRequest[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [processingId, setProcessingId] = useState<string | null>(null)

  // Inline price input state (for expert "accepted" tab)
  const [inlinePriceInputs, setInlinePriceInputs] = useState<Record<string, string>>({})
  const [inlinePriceMessages, setInlinePriceMessages] = useState<Record<string, string>>({})

  // Drawer state
  const [selectedRequest, setSelectedRequest] = useState<ServiceRequest | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Map tabs to status filters
  const getStatusFilter = (tab: Tab): ServiceRequestStatus | undefined => {
    switch (tab) {
      case 'new':
        return 'new'
      case 'accepted':
        return 'accepted'
      case 'awaiting_payment':
        return 'awaiting_payment'
      case 'in_progress':
        return 'in_progress'
      case 'history':
        return undefined // Will filter client-side for terminal states
      default:
        return undefined
    }
  }

  const { fetchCounts } = useNotificationStore()

  // Fetch data based on tab
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      setError(null)

      try {
        // Mark tab as viewed (skip history tab)
        if (activeTab !== 'history') {
          await serviceRequestService.markTabViewed(activeTab)
          fetchCounts()
        }

        const statusFilter = getStatusFilter(activeTab)
        let response
        if (statusFilter) {
          response = await serviceRequestService.getRequests(statusFilter)
        } else {
          response = await serviceRequestService.getRequests()
        }

        let filteredResults = response.results || []

        // For 'history' tab, filter client-side for terminal states
        if (activeTab === 'history') {
          const terminalStatuses: ServiceRequestStatus[] = [
            'completed',
            'declined',
            'declined_requester',
            'refunded',
          ]
          filteredResults = filteredResults.filter((r) => terminalStatuses.includes(r.status))
        }

        setRequests(filteredResults)
      } catch (err: unknown) {
        console.error('Error fetching service requests:', err)
        const error = err as { response?: { data?: { detail?: string } } }
        setError(error.response?.data?.detail || 'Failed to load requests')
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [activeTab, fetchCounts])

  // Handle payment return from Stripe
  useEffect(() => {
    const payment = searchParams.get('payment')
    if (payment === 'success') {
      const sessionId = searchParams.get('session_id')
      // Clear the URL params immediately to prevent re-triggering
      setSearchParams({})

      if (sessionId) {
        // Verify the payment with our backend
        serviceRequestService
          .verifyPayment(sessionId)
          .then((result) => {
            if (result.status === 'verified' || result.status === 'already_completed') {
              toast.success('Payment successful! You can now message this expert.')
            } else {
              toast.success('Payment is being processed. It may take a moment to update.')
            }
          })
          .catch(() => {
            // Graceful fallback: webhook will still process it
            toast.success('Payment is being processed. It may take a moment to update.')
          })
      } else {
        toast.success('Payment successful! You can now message this expert.')
      }
      setActiveTab('in_progress')
    } else if (payment === 'cancelled') {
      toast.error('Payment was cancelled.')
      setSearchParams({})
    }
  }, [searchParams, setSearchParams])

  const handleSetPrice = async (requestId: string) => {
    const priceStr = inlinePriceInputs[requestId]
    const priceEur = parseFloat(priceStr)
    if (isNaN(priceEur) || priceEur < 1) {
      toast.error('Minimum price is €1.00')
      return
    }

    setProcessingId(requestId)
    try {
      await serviceRequestService.setPrice(requestId, {
        price_cents: Math.round(priceEur * 100),
        message: inlinePriceMessages[requestId]?.trim() || undefined,
      })
      toast.success('Price set! Waiting for payment.')
      setRequests((prev) => prev.filter((r) => r.id !== requestId))
      setInlinePriceInputs((prev) => { const n = { ...prev }; delete n[requestId]; return n })
      setInlinePriceMessages((prev) => { const n = { ...prev }; delete n[requestId]; return n })
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string; detail?: string } } }
      toast.error(error.response?.data?.error || error.response?.data?.detail || 'Failed to set price')
    } finally {
      setProcessingId(null)
    }
  }

  const handleDeclinePrice = async (requestId: string) => {
    setProcessingId(requestId)
    try {
      await serviceRequestService.declinePrice(requestId)
      toast.success('Price declined')
      setRequests((prev) => prev.filter((r) => r.id !== requestId))
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string; detail?: string } } }
      toast.error(error.response?.data?.error || error.response?.data?.detail || 'Failed to decline')
    } finally {
      setProcessingId(null)
    }
  }

  const handlePay = async (requestId: string) => {
    setProcessingId(requestId)
    try {
      const { checkout_url } = await serviceRequestService.createCheckout(requestId)
      window.location.href = checkout_url
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string; detail?: string } } }
      toast.error(error.response?.data?.error || error.response?.data?.detail || 'Failed to create checkout')
      setProcessingId(null)
    }
  }

  const handleMarkCompleted = async (requestId: string) => {
    setProcessingId(requestId)
    try {
      await serviceRequestService.markCompleted(requestId)
      toast.success('Session marked as completed!')
      setRequests((prev) => prev.filter((r) => r.id !== requestId))
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string; detail?: string } } }
      toast.error(error.response?.data?.error || error.response?.data?.detail || 'Failed to mark complete')
    } finally {
      setProcessingId(null)
    }
  }

  const openDrawer = (request: ServiceRequest) => {
    setSelectedRequest(request)
    setDrawerOpen(true)
  }

  const closeDrawer = () => {
    setDrawerOpen(false)
    setTimeout(() => setSelectedRequest(null), 300)
  }

  const handleDrawerAccept = async (id: string, data: DeclineRequest) => {
    setProcessingId(id)
    try {
      await serviceRequestService.accept(id, data)
      toast.success('Request accepted! You can now message the requester.')
      setRequests((prev) => prev.filter((r) => r.id !== id))
      closeDrawer()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string; detail?: string } } }
      toast.error(error.response?.data?.error || error.response?.data?.detail || 'Failed to accept')
    } finally {
      setProcessingId(null)
    }
  }

  const handleDrawerDecline = async (id: string, data: DeclineRequest) => {
    setProcessingId(id)
    try {
      await serviceRequestService.decline(id, data)
      toast.success('Request declined')
      setRequests((prev) => prev.filter((r) => r.id !== id))
      closeDrawer()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string; detail?: string } } }
      toast.error(error.response?.data?.error || error.response?.data?.detail || 'Failed to decline')
    } finally {
      setProcessingId(null)
    }
  }

  const tabs: { id: Tab; label: string; icon: React.ElementType }[] = isTalent
    ? [
        { id: 'new', label: 'New Requests', icon: Inbox },
        { id: 'accepted', label: 'Active', icon: MessageSquare },
        { id: 'awaiting_payment', label: 'Awaiting Payment', icon: Hourglass },
        { id: 'in_progress', label: 'In Progress', icon: CheckCircle },
        { id: 'history', label: 'History', icon: CheckCircle },
      ]
    : [
        { id: 'new', label: 'Pending', icon: Clock },
        { id: 'accepted', label: 'Active', icon: MessageSquare },
        { id: 'awaiting_payment', label: 'Ready to Pay', icon: CreditCard },
        { id: 'in_progress', label: 'In Progress', icon: CheckCircle },
        { id: 'history', label: 'History', icon: CheckCircle },
      ]

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const getStatusBadge = (status: ServiceRequestStatus) => {
    const badges: Record<ServiceRequestStatus, { className: string; label: string; icon: React.ElementType }> = {
      new: { className: 'badge-warning', label: 'New', icon: Clock },
      accepted: { className: 'badge-primary', label: 'Accepted', icon: CheckCircle },
      awaiting_payment: { className: 'badge-primary', label: 'Awaiting Payment', icon: CreditCard },
      declined: { className: 'badge-danger', label: 'Declined', icon: XCircle },
      declined_requester: { className: 'badge-danger', label: 'Price Declined', icon: XCircle },
      in_progress: { className: 'badge-success', label: 'In Progress', icon: CheckCircle },
      completed: { className: 'badge-success', label: 'Completed', icon: CheckCircle },
      refunded: { className: 'badge-secondary', label: 'Refunded', icon: Clock },
    }
    const badge = badges[status] || { className: 'badge-secondary', label: status, icon: Clock }
    const Icon = badge.icon
    return (
      <span className={`badge ${badge.className} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {badge.label}
      </span>
    )
  }

  const renderEmptyState = () => {
    const emptyMessages: Record<Tab, string> = {
      new: isTalent
        ? 'No new service requests. When seekers want to work with you, their requests will appear here.'
        : 'No pending requests. Request a service from an expert profile to get started.',
      accepted: isTalent
        ? 'No active collaborations. Accept a request to start messaging.'
        : 'No active collaborations. Once an expert accepts your request, you can message here.',
      awaiting_payment: isTalent
        ? 'No requests awaiting payment.'
        : 'No quotes received yet. Once an expert sets a price, you can pay here.',
      in_progress: isTalent
        ? 'No sessions in progress.'
        : 'No sessions in progress. Once you pay, the session will appear here.',
      history: 'No completed or archived requests.',
    }

    const emptyIcons: Record<Tab, React.ElementType> = {
      new: Inbox,
      accepted: MessageSquare,
      awaiting_payment: Hourglass,
      in_progress: CheckCircle,
      history: CheckCircle,
    }

    const Icon = emptyIcons[activeTab]

    return (
      <div className="card">
        <div className="card-body py-16">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 rounded-full bg-secondary-100 flex items-center justify-center mb-4">
              <Icon className="h-8 w-8 text-secondary-400" />
            </div>
            <p className="text-secondary-600 max-w-md">{emptyMessages[activeTab]}</p>
            {activeTab === 'new' && !isTalent && (
              <Link to="/talents" className="btn btn-accent mt-4">
                Find Experts
              </Link>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderRequestCard = (request: ServiceRequest) => {
    const isProcessing = processingId === request.id
    const isNew = request.status === 'new'
    const isAccepted = request.status === 'accepted'
    const isAwaitingPayment = request.status === 'awaiting_payment'
    const isInProgress = request.status === 'in_progress'
    const isTerminal = ['completed', 'declined', 'declined_requester', 'refunded'].includes(
      request.status
    )

    // Talent can click to open drawer for new requests
    const isClickable = isTalent && isNew

    return (
      <div
        key={request.id}
        data-testid="request-card"
        className={`card hover:shadow-lg transition-shadow ${isClickable ? 'cursor-pointer' : ''}`}
        onClick={isClickable ? () => openDrawer(request) : undefined}
      >
        <div className="card-body">
          <div className="flex flex-col gap-4">
            {/* Header row */}
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              {/* User info */}
              <div className="flex items-start gap-4">
                {isTalent && request.seeker?.profile_picture_url ? (
                  <img
                    src={request.seeker.profile_picture_url}
                    alt={request.requester_full_name || request.seeker?.company_name || 'Seeker'}
                    className="h-14 w-14 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                <div className="h-14 w-14 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                  {isTalent
                    ? (request.requester_full_name?.[0] || request.seeker?.company_name?.[0] || 'S').toUpperCase()
                    : (request.talent?.full_name?.[0] || 'T').toUpperCase()}
                </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-lg text-secondary-900">
                      {isTalent
                        ? request.requester_full_name || `${request.seeker?.user_first_name || ''} ${request.seeker?.user_last_name || ''}`.trim() || request.seeker?.company_name || 'Unknown'
                        : request.talent?.full_name || 'Unknown'}
                    </h3>
                    {getStatusBadge(request.status)}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-secondary-500 mt-1">
                    {isTalent ? (
                      <>
                        <span className="flex items-center gap-1">
                          <Briefcase className="h-3.5 w-3.5" />
                          {request.seeker?.industry || 'Industry not specified'}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" />
                          {request.seeker?.country || 'Location not specified'}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="flex items-center gap-1">
                          <Briefcase className="h-3.5 w-3.5" />
                          {request.talent?.headline || 'Expert'}
                        </span>
                      </>
                    )}
                    {request.request_type && (
                      <span className="text-primary-600 font-medium">
                        {REQUEST_TYPE_OPTIONS.find((o) => o.value === request.request_type)?.label || request.request_type}
                      </span>
                    )}
                    {request.service_type && (
                      <span className="text-primary-600 font-medium">
                        {request.service_type.name}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Price display */}
              {request.price_display && (
                <div className="text-lg font-semibold text-accent-600">
                  {request.price_display}
                </div>
              )}
            </div>

            {/* Message */}
            <div className="p-3 bg-secondary-50 rounded-lg">
              <p className="text-sm text-secondary-700">"{request.message}"</p>
            </div>

            {/* Expert response */}
            {request.expert_message && (
              <div className="p-3 bg-primary-50 rounded-lg border border-primary-100">
                <p className="text-sm text-primary-700">
                  <span className="font-medium">Expert response:</span> "{request.expert_message}"
                </p>
              </div>
            )}

            {/* Refund reason */}
            {request.status === 'refunded' && request.refund_reason && (
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="text-sm text-red-700">
                  <span className="font-medium">Refund reason:</span> {request.refund_reason}
                </p>
              </div>
            )}

            {/* Inline price input for expert "accepted" tab */}
            {isAccepted && isTalent && (
              <div className="p-4 bg-secondary-50 rounded-lg border border-secondary-200">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <label className="label text-xs mb-1">Your price (EUR)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400 font-medium">€</span>
                      <input
                        type="number"
                        min="1"
                        step="0.01"
                        placeholder="Set your price"
                        value={inlinePriceInputs[request.id] || ''}
                        onChange={(e) => setInlinePriceInputs((prev) => ({ ...prev, [request.id]: e.target.value }))}
                        className="input pl-8 w-full"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <label className="label text-xs mb-1">Message (optional)</label>
                    <input
                      type="text"
                      placeholder="Add a note..."
                      value={inlinePriceMessages[request.id] || ''}
                      onChange={(e) => setInlinePriceMessages((prev) => ({ ...prev, [request.id]: e.target.value }))}
                      className="input w-full"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleSetPrice(request.id) }}
                      disabled={isProcessing || !inlinePriceInputs[request.id] || parseFloat(inlinePriceInputs[request.id]) < 1}
                      className="btn btn-accent flex items-center gap-1.5 whitespace-nowrap"
                    >
                      {isProcessing ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CreditCard className="h-4 w-4" />
                      )}
                      Set Price
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Meta info */}
            <div className="flex items-center gap-4 text-xs text-secondary-400">
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                Requested {formatDate(request.created_at)}
              </span>
              {request.accepted_at && (
                <span>Accepted {formatDate(request.accepted_at)}</span>
              )}
              {request.responded_at && !request.accepted_at && (
                <span>Responded {formatDate(request.responded_at)}</span>
              )}
              {request.paid_at && <span>Paid {formatDate(request.paid_at)}</span>}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-secondary-100">
              {/* Expert: New - Open drawer to review */}
              {isNew && isTalent && (
                <button
                  onClick={(e) => { e.stopPropagation(); openDrawer(request) }}
                  className="btn btn-accent flex items-center gap-1.5"
                >
                  <Check className="h-4 w-4" />
                  Review & Respond
                </button>
              )}

              {/* Seeker: New - Waiting */}
              {isNew && !isTalent && (
                <span className="text-secondary-500 text-sm">
                  Waiting for expert to respond...
                </span>
              )}

              {/* Expert: Accepted - Message link (price input is inline above) */}
              {isAccepted && isTalent && (
                <button
                  onClick={(e) => { e.stopPropagation(); navigate('/messages') }}
                  className="btn btn-primary flex items-center gap-1.5"
                >
                  <MessageSquare className="h-4 w-4" />
                  Message
                </button>
              )}

              {/* Seeker: Accepted - Message link */}
              {isAccepted && !isTalent && (
                <button
                  onClick={() => navigate('/messages')}
                  className="btn btn-primary flex items-center gap-1.5"
                >
                  <MessageSquare className="h-4 w-4" />
                  Message
                </button>
              )}

              {/* Expert: Awaiting payment - Waiting */}
              {isAwaitingPayment && isTalent && (
                <>
                  <span className="text-secondary-500 text-sm flex items-center gap-1">
                    <Hourglass className="h-4 w-4" />
                    Waiting for payment
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate('/messages') }}
                    className="btn btn-primary flex items-center gap-1.5"
                  >
                    <MessageSquare className="h-4 w-4" />
                    Message
                  </button>
                </>
              )}

              {/* Seeker: Awaiting payment - Pay or decline */}
              {isAwaitingPayment && !isTalent && (
                <>
                  <button
                    onClick={() => handlePay(request.id)}
                    disabled={isProcessing}
                    className="btn btn-accent flex items-center gap-1.5"
                  >
                    {isProcessing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CreditCard className="h-4 w-4" />
                    )}
                    Pay {request.price_display}
                  </button>
                  <button
                    onClick={() => handleDeclinePrice(request.id)}
                    disabled={isProcessing}
                    className="btn btn-secondary flex items-center gap-1.5"
                  >
                    <X className="h-4 w-4" />
                    Decline Price
                  </button>
                </>
              )}

              {/* In Progress - Message + Mark Complete (seeker only) */}
              {isInProgress && (
                <>
                  <button
                    onClick={() => navigate('/messages')}
                    className="btn btn-primary flex items-center gap-1.5"
                  >
                    <MessageSquare className="h-4 w-4" />
                    Message
                  </button>
                  {!isTalent && (
                    <button
                      onClick={() => handleMarkCompleted(request.id)}
                      disabled={isProcessing}
                      className="btn btn-accent flex items-center gap-1.5"
                    >
                      {isProcessing ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle className="h-4 w-4" />
                      )}
                      Mark Complete
                    </button>
                  )}
                </>
              )}

              {/* Terminal states - View profile link */}
              {isTerminal && !isTalent && request.talent && (
                <Link
                  to={`/talents/${request.talent.id}`}
                  className="btn btn-secondary flex items-center gap-1.5"
                >
                  View Profile
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-secondary-900">My Collaborations</h1>
        <p className="text-secondary-600 mt-1">
          {isTalent
            ? 'Review incoming requests and manage your collaborations'
            : 'Track your collaboration requests and payments'}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-secondary-100 p-1 rounded-xl w-fit mb-6 overflow-x-auto">
        {tabs.map((tab) => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-white shadow-sm text-primary-700'
                  : 'text-secondary-600 hover:text-secondary-900 hover:bg-secondary-50'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          )
        })}
      </div>

      {/* Error state */}
      {error && (
        <div className="card border-red-200 bg-red-50 mb-6">
          <div className="card-body">
            <div className="flex items-center gap-3 text-red-700">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="card">
          <div className="card-body py-16">
            <div className="flex flex-col items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600 mb-4" />
              <p className="text-secondary-600">Loading requests...</p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {!isLoading && !error && (
        <div className="space-y-4">
          {requests.length > 0 ? requests.map(renderRequestCard) : renderEmptyState()}
        </div>
      )}

      {/* Request Detail Drawer (for talent/expert users - new requests only) */}
      {isTalent && (
        <RequestDetailDrawer
          request={selectedRequest}
          isOpen={drawerOpen}
          onClose={closeDrawer}
          onAccept={handleDrawerAccept}
          onDecline={handleDrawerDecline}
          isProcessing={!!processingId}
        />
      )}
    </div>
  )
}
